// Maps a specific sub-course's display name (as it appears in a parent
// category's `subCourses` list, or in old-app sourced description text) to
// its own flat slug in src/mocks/siteData.ts's `courses` array. Single
// shared source — previously duplicated inline in course-detail/page.tsx;
// also used by course-category/page.tsx so both pages resolve sub-course
// links identically without redefining this mapping twice.
export const courseNameToSlug: Record<string, string> = {
  "Sports Climbing Course": "sports-climbing",
  "Basic Mountaineering Course (BMC)": "basic-mountaineering-course",
  "Advanced Mountaineering Course (AMC)": "advanced-mountaineering-course",
  "Search & Rescue Course (S&R)": "search-rescue-mountaineering",
  "Method of Instruction (MOI)": "method-of-instruction-mountaineering",
  "Method of Instruction Course (MOI)": "method-of-instruction-mountaineering",
  "Advance Mountaineering Course (AMC)": "advanced-mountaineering-course",
  "Basic Mountain Terrain Biking (BMTB)": "basic-mountain-terrain-biking",
  "Advance Mountain Terrain Biking Course (AMTB)": "advanced-mountain-terrain-biking",
  "Advanced Mountain Terrain Biking (AMTB)": "advanced-mountain-terrain-biking",
  "Scuba Diving — Basic": "scuba-diving-basic",
  "Scuba Diving — Advanced": "scuba-diving-advanced",
  "Search & Rescue Scuba": "search-rescue-scuba",
  "Scuba Diving — Combined": "scuba-combined",
  "White Water Rafting — Basic": "white-water-rafting-basic",
  "White Water Rafting — Intermediate": "white-water-rafting-intermediate",
  "Basic White Water Rafting Course (BWWR)": "white-water-rafting-basic",
  "Intermediate White Water Rafting Course (IWWR)": "white-water-rafting-intermediate",
  "Kayaking — Basic": "kayaking-basic",
  "Paragliding — Basic & Intermediate (P1 & P2)": "paragliding-basic-intermediate",
  "Basic Paragliding Course (P1 & P2)": "paragliding-basic-intermediate",
  "Advance Paragliding Course (P3)": "paragliding-advanced",
  "Combined Paragliding Course (P1, P2 & P3)": "paragliding-combined",
  "Paragliding — Advanced (P3)": "paragliding-advanced",
  "Paragliding — Combined (P1, P2 & P3)": "paragliding-combined",
  "Paragliding — Refresher": "paragliding-refresher",
  "Paramotor — Basic": "paramotor-basic",
  "Paramotor — Intermediate": "paramotor-intermediate",
  "Paramotor Trike": "paramotor-trike",
  "Basic Paramotor Course (BPMC)": "paramotor-basic",
  "Intermediate Paramotor Course (IPMC)": "paramotor-intermediate",
  "Paramotor Trike Course (PMTC)": "paramotor-trike",
  "Basic Scuba Diving Course (BSDC)": "scuba-diving-basic",
  "Advance Scuba Diving Course (ASDC)": "scuba-diving-advanced",
  "Search & Rescue Scuba Diving Course (S&RSDC)": "search-rescue-scuba",
  "Combined Scuba Diving Course": "scuba-combined",
};

// The live backend's category list (templateID 2 "Courselist") includes
// "Sport Climbing" and "White Water Kayaking" as their own distinct category
// rows (see courseService.ts's SLUG_TO_REAL_CATEGORY comment) — the /courses
// listing page slugifies those live names verbatim for its card links
// ("sport-climbing", "white-water-kayaking"), but the actual course content
// in siteData.ts predates that live category split and still uses
// "sports-climbing" and "kayaking". Without this alias, following either
// card 404s with "Course Not Found" even though the content exists under a
// different slug. Resolve at lookup time rather than renaming the siteData
// slugs, since "sports-climbing"/"kayaking" are keyed on elsewhere
// (batchService.ts's SLUG_TO_CATEGORY, BatchSelection.tsx's courseTabGroups,
// etc.) and renaming them would ripple through those too.
const SLUG_ALIASES: Record<string, string> = {
  "sport-climbing": "sports-climbing",
  "white-water-kayaking": "kayaking",
};

export function resolveCourseSlug(slug: string): string {
  return SLUG_ALIASES[slug] ?? slug;
}
